import React, { FC, useState } from "react";
import { useLocation } from "react-router-dom";
import MainLayout from "../../Layouts/MainLayout";
import Label from "../atoms/Label";
import RadioButton from "../atoms/RadioButton";
import Button from "../atoms/Button";
import SectionHeader from "../molecules/SectionHeader";
import WindowHeightComponent from "../../Layouts/WindowHeight";

interface FrequencyState {
  usageType: "manual" | "automatic" | "";
  frequency: string;
  customFrequencyText: string;
  customFrequencyNo: number;
  time: string;
  additionalTimes: string[];
  fromDate?: Date;
  toDate?: Date;
}

const Frequency: FC = () => {
  const location = useLocation();
  const { crewName } = location.state || {};

  const [state, setState] = useState<FrequencyState>({
    usageType: "",
    frequency: "",
    customFrequencyText: "",
    customFrequencyNo: 0,
    time: "",
    additionalTimes: [],
    fromDate: undefined,
    toDate: undefined,
  });
  // Handle the change of usage type (manual/automatic)
  const handleUsageTypeChange = (type: "manual" | "automatic") => {
    setState((prev) => ({
      ...prev,
      usageType: type,
      frequency: type === "manual" ? "" : prev.frequency,
      customFrequencyText: type === "manual" ? "" : prev.customFrequencyText,
      customFrequencyNo: prev.customFrequencyNo,
      time: type === "manual" ? "" : prev.time,
      additionalTimes: [],
      fromDate: undefined,
      toDate: undefined,
    }));
  };
  // Handle the change of frequency type
  const handleFrequencyChange = (key: string) => {
    setState((prev) => ({
      ...prev,
      frequency: key,
      fromDate: undefined,
      toDate: undefined,
    }));
  };
  // Handle the change of date inputs
  const handleDateChange = (key: "fromDate" | "toDate", date: Date) => {
    setState((prev) => ({
      ...prev,
      [key]: date,
    }));
  };
  // Add additional time for automatic usage
  const handleAddTime = () => {
    setState((prev) => ({
      ...prev,
      additionalTimes: [...prev.additionalTimes, ""],
    }));
  };
  // Update the time at a specific index
  const handleTimeChange = (index: number, value: string) => {
    const updatedTimes = [...state.additionalTimes];
    updatedTimes[index] = value;
    setState((prev) => ({
      ...prev,
      additionalTimes: updatedTimes,
    }));
  };

  const handleSave = () => {
    console.log("Saving frequency settings:", state);
  };

  return (
    <MainLayout>

      <SectionHeader back={true} title={`Frequency of crew usage`}></SectionHeader>
      <WindowHeightComponent className={""}>
        <div className="row m-0 p-3">
          {/* Usage Type Section */}
          <div className="col-4 ps-0">
            <Label className="mb-4 font-medium">Usage type</Label>
            <div>
              <RadioButton
                name="usageType"
                checked={state.usageType === "manual"}
                onChange={() => handleUsageTypeChange("manual")}
                label="Manual"
              />
              <RadioButton
                name="usageType"
                checked={state.usageType === "automatic"}
                onChange={() => handleUsageTypeChange("automatic")}
                label="Automatic"
              />
            </div>
          </div>

          {/* Frequency Section */}
          {state.usageType === "automatic" && (
            <div className="col-4">
              <Label className="mb-4 font-medium">
                When do you want to use the crew?
              </Label>
              <div>
                <RadioButton
                  name="frequency"
                  checked={state.frequency === "daily"}
                  onChange={() => handleFrequencyChange("daily")}
                  label="Daily"
                />
                <div className="d-flex align-items-center">
                  <RadioButton
                    name="frequency"
                    checked={state.frequency === "oddDays"}
                    onChange={() => handleFrequencyChange("oddDays")}
                    label="Odd days"
                  />
                  {state.frequency === "oddDays" && (
                    <div className="d-flex align-items-center ms-2">
                      <input
                        type="date"
                        className="form-control me-2"
                        placeholder="From"
                        value={state.fromDate?.toISOString().slice(0, 10) || ""}
                        onChange={(e) =>
                          handleDateChange("fromDate", new Date(e.target.value))
                        }
                      />
                      <input
                        type="date"
                        className="form-control"
                        placeholder="To"
                        value={state.toDate?.toISOString().slice(0, 10) || ""}
                        onChange={(e) =>
                          handleDateChange("toDate", new Date(e.target.value))
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-center">
                  <RadioButton
                    name="frequency"
                    checked={state.frequency === "evenDays"}
                    onChange={() => handleFrequencyChange("evenDays")}
                    label="Even days"
                  />
                  {state.frequency === "evenDays" && (
                    <div className="d-flex align-items-center ms-2">
                      <input
                        type="date"
                        className="form-control me-2"
                        placeholder="From"
                        value={state.fromDate?.toISOString().slice(0, 10) || ""}
                        onChange={(e) =>
                          handleDateChange("fromDate", new Date(e.target.value))
                        }
                      />
                      <input
                        type="date"
                        className="form-control"
                        placeholder="To"
                        value={state.toDate?.toISOString().slice(0, 10) || ""}
                        onChange={(e) =>
                          handleDateChange("toDate", new Date(e.target.value))
                        }
                      />
                    </div>
                  )}
                </div>
                <RadioButton
                  name="frequency"
                  checked={state.frequency === "weekly"}
                  onChange={() => handleFrequencyChange("weekly")}
                  label="Weekly"
                />
                <RadioButton
                  name="frequency"
                  checked={state.frequency === "customFrequency"}
                  onChange={() => handleFrequencyChange("customFrequency")}
                  label="Custom frequency"
                />
                {state.frequency === "customFrequency" && (
                  <>
                    <input
                      type="text"
                      className="form-control mt-2"
                      placeholder="Enter custom frequency"
                      value={state.customFrequencyText}
                      onChange={(e) =>
                        setState((prev) => ({
                          ...prev,
                          customFrequencyText: e.target.value,
                        }))
                      }
                    />
                    <div className="d-flex justify-content-center">Or</div>
                    <Label>Select an instance to run the crew</Label>
                    <input
                      type="number"
                      className="form-control mt-2"
                      value={state.customFrequencyNo}
                      onChange={(e) =>
                        setState((prev) => ({
                          ...prev,
                          customFrequencyNo: Number(e.target.value)
                        }))
                      }
                    />
                  </>
                )}


              </div>
            </div>
          )}

          {/* Timing Section */}
          {state.usageType === "automatic" && (
            <div className="col-4">
              <div className="d-flex align-items-center">
                <Label className="me-2 font-medium">Select time</Label>
                <input
                  type="time"
                  className="form-control"
                  style={{ maxWidth: "150px" }}
                  value={state.time}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, time: e.target.value }))
                  }
                />
                <Button
                  className="btn btn-sm btn-secondary ms-2"
                  onClick={handleAddTime}
                >
                  +
                </Button>
              </div>

              {state.additionalTimes.length > 0 && (
                <div className="mt-3 ">
                  {state.additionalTimes.map((time, index) => (
                    <div
                      className="d-flex justify-content-center  mb-2"
                      style={{ marginRight: "110px" }}
                      key={index}
                    >
                      <input
                        type="time"
                        className="form-control"
                        style={{ maxWidth: "150px" }}
                        value={time}
                        onChange={(e) =>
                          handleTimeChange(index, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-end mt-4 px-0">
            {state.usageType === "automatic" && (
              <Button className="btn btn-secondary me-2">Cancel</Button>
            )}
            <Button className="btn SaveBtn" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>

      </WindowHeightComponent>
    </MainLayout>
  );
};

export default Frequency;
